import { electron } from 'lib-shared-electron';
import { ampli, IdentifyProperties } from 'lib-shared-tracking';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useMount } from 'react-use';

import {
  getLocalSettings,
  updateSettings,
} from 'components/settings/api/personal';
import { useServerStorage } from 'utils/useServerStorage';
import { useCurrentUser } from 'utils/user';

import { type ThemeContextValue, type ThemePreference } from './theme.types';
import { applyTheme } from './theme.utils';

const localStorageKey = 'theme';

const ThemeContext = createContext<ThemeContextValue>(undefined!);

export function ThemeProvider({ children }: PropsWithChildren): JSX.Element {
  const storage = useServerStorage('user');
  const { user } = useCurrentUser();

  const isThemingSupported = useMemo(() => !electron.isElectron(), []);

  /**
   * The current theme preference, resolved either from serverStorage or
   * localStorage.
   */
  const preferredTheme = useMemo((): ThemePreference => {
    if (isThemingSupported) {
      return storage.data.global?.theme ?? { id: 'default' };
    }
    return { id: 'classic' };
  }, [isThemingSupported, storage.data.global?.theme]);

  useMount(() => {
    applyTheme(preferredTheme);

    // Ensures that the value in localStorage is in sync with the serverStorage.
    if (preferredTheme.id !== 'custom') {
      const localThemeId = getLocalSettings()?.[localStorageKey];

      if (localThemeId !== preferredTheme.id) {
        updateSettings({ key: localStorageKey, value: preferredTheme.id });
      }
    }
  });

  // Updates the user's theme in Amplitude whenever it changes.
  useEffect(() => {
    ampli.identify(user.id, { theme: preferredTheme.id } as IdentifyProperties);
  }, [preferredTheme.id, user.id]);

  /**
   * Previews a theme by applying it to the document's HTML element. This is
   * useful for providing a live preview of the selected theme before the user
   * confirms their choice.
   */
  const previewTheme = useCallback((theme: ThemePreference) => {
    applyTheme(theme);
  }, []);

  /**
   * Persists the specified theme by updating the serverStorage and
   * localStorage. This ensures that the user's preference is saved and applied
   * across sessions.
   */
  const persistTheme = useCallback(
    (theme: ThemePreference, { skipServerStorage = false } = {}) => {
      applyTheme(theme);

      if (theme.id !== 'custom') {
        updateSettings({ key: localStorageKey, value: theme.id });
      }

      if (!skipServerStorage) {
        storage.set({
          global: (draft) => {
            draft.theme = theme;
          },
        });
      }
    },
    [storage],
  );

  /** Cancels any active preview and restores the persisted theme. */
  const cancelPreview = useCallback(() => {
    applyTheme(preferredTheme);
  }, [preferredTheme]);

  const contextValue: ThemeContextValue = useMemo(
    () => ({
      theme: preferredTheme,
      isThemingSupported,
      persistTheme,
      previewTheme,
      cancelPreview,
    }),
    [
      isThemingSupported,
      preferredTheme,
      persistTheme,
      previewTheme,
      cancelPreview,
    ],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
